import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import adminService from '../../../services/adminService';

const AddCreditsModal = ({ onClose, onSuccess, preselectedUser = null }) => {
  const [selectedUserId, setSelectedUserId] = useState(preselectedUser?.id || '');
  const [creditAmount, setCreditAmount] = useState('');
  const [reason, setReason] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(preselectedUser || null);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (preselectedUser) {
      setSelectedUserId(preselectedUser?.id);
      setSelectedUserData(preselectedUser);
    }
  }, [preselectedUser]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const verifiedUsers = await adminService?.getVerifiedUsers();
      setUsers(verifiedUsers || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserChange = (e) => {
    const userId = e?.target?.value;
    setSelectedUserId(userId);
    
    // Find and store the full user data for the selected user
    const userData = users?.find(u => u?.value === userId);
    setSelectedUserData(userData || null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!selectedUserId || !creditAmount || parseInt(creditAmount) <= 0) {
      setError('Please select a user and enter a valid credit amount');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await adminService?.addCreditsToUser(
        selectedUserId, 
        parseInt(creditAmount), 
        reason || 'Admin credit grant'
      );

      onSuccess?.(result);
      onClose?.();
    } catch (err) {
      console.error('Credit addition error:', err);
      setError(err?.message || 'Failed to add credits. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentBalance = () => {
    if (preselectedUser) {
      return preselectedUser?.credits || 0;
    }
    return selectedUserData?.credits || 0;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="Coins" size={20} color="#10B981" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Add Credits</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} color="#DC2626" className="mt-0.5" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {preselectedUser && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} color="#2563EB" className="mt-0.5" />
                <p className="text-sm text-blue-800">
                  Adding credits to: <span className="font-semibold">{preselectedUser?.name}</span>
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select User {preselectedUser && <span className="text-gray-400">(preselected)</span>}
            </label>
            <select
              value={selectedUserId}
              onChange={handleUserChange}
              disabled={loadingUsers || !!preselectedUser}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
            >
              <option value="">
                {loadingUsers ? 'Loading users...' : 'Select a user'}
              </option>
              {users?.map((user) => (
                <option key={user?.value} value={user?.value}>
                  {user?.label}
                </option>
              ))}
            </select>
            {selectedUserData && (
              <p className="mt-2 text-xs text-gray-600">
                {selectedUserData?.role === 'admin' 
                  ? <span className="text-blue-600 font-medium">Admin account — has unlimited credits</span>
                  : <>Current balance: <span className="font-semibold">{getCurrentBalance()} credits</span></>
                }
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Credits to Add *
            </label>
            <Input
              type="number"
              min="1"
              step="1"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e?.target?.value)}
              placeholder="Enter number of credits"
              required
            />
            {creditAmount && selectedUserData && (
              <p className="mt-2 text-sm text-gray-600">
                {selectedUserData?.role === 'admin'
                  ? <span className="text-blue-600">Admin accounts have unlimited credits</span>
                  : <>New balance will be: <span className="font-semibold text-green-600">
                      {parseInt(creditAmount) + getCurrentBalance()} credits
                    </span></>
                }
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e?.target?.value)}
              placeholder="Enter reason for adding credits (e.g., promotional grant, compensation, etc.)"
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Important:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Credits can only be added to verified accounts</li>
                  <li>This action will be logged in the activity history</li>
                  <li>Credits are immediately available to the user</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={loading}
              disabled={loading || !selectedUserId || !creditAmount}
            >
              {loading ? 'Adding Credits...' : 'Add Credits'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCreditsModal;